import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BrandService } from '@admin-core/services/brand.service';
import { Brand } from '@admin-core/models/brand.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';
import { ImgFallbackDirective } from '@admin-shared/directives/img-fallback.directive';
import { of, switchMap } from 'rxjs';

@Component({
  selector: 'app-brands-admin',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, ImgFallbackDirective],
  templateUrl: './brands-admin.component.html',
  styleUrl: './brands-admin.component.scss'
})
export class BrandsAdminComponent implements OnInit {
  private readonly api = inject(BrandService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<Brand[]>([]);
  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly logoPreview = signal<string | null>(null);
  readonly pendingLogoFile = signal<File | null>(null);
  readonly pendingRemoveLogo = signal(false);

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    isActive: [true]
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getAll().subscribe({
      next: (items) => {
        this.items.set(items);
        this.loading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('brands.loadError'));
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form.reset({ name: '', isActive: true });
    this.resetLogoState();
    this.dialogOpen.set(true);
  }

  openEdit(item: Brand): void {
    this.editingId.set(item.id);
    this.form.setValue({
      name: item.name,
      isActive: item.isActive
    });
    this.resetLogoState(item.logoUrl ?? null);
    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.resetLogoState();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) {
      return;
    }

    this.pendingLogoFile.set(file);
    this.pendingRemoveLogo.set(false);
    this.logoPreview.set(URL.createObjectURL(file));
  }

  clearLogo(): void {
    this.pendingLogoFile.set(null);
    this.pendingRemoveLogo.set(true);
    this.logoPreview.set(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.form.getRawValue();
    const id = this.editingId();
    const req$ = id ? this.api.update(id, payload) : this.api.create(payload);

    req$
      .pipe(
        switchMap((brand) => {
          const file = this.pendingLogoFile();
          if (file) {
            return this.api.uploadLogo(brand.id, file);
          }
          if (this.pendingRemoveLogo() && this.editingId()) {
            return this.api.deleteLogo(brand.id);
          }
          return of(brand);
        })
      )
      .subscribe({
        next: () => {
          this.closeDialog();
          this.load();
        },
        error: (err) =>
          this.showError(err?.error?.error ?? this.i18n.t('brands.saveError'))
      });
  }

  async remove(item: Brand): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('brands.deleteConfirm', { name: item.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.api.delete(item.id).subscribe({
      next: () => this.load(),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('brands.deleteError'))
    });
  }

  private resetLogoState(preview: string | null = null): void {
    this.pendingLogoFile.set(null);
    this.pendingRemoveLogo.set(false);
    this.logoPreview.set(preview);
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
